/**
 * CRM Client Deadlines Page - ATO deadlines with compliance traffic light.
 */

import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CalendarDays, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { crmPortalService } from '@/services/api/crm-portal';
import { cn } from '@/lib/utils';

export function CRMDeadlinesPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['crm-deadlines'],
    queryFn: () => crmPortalService.getDeadlines(),
  });

  const deadlines = data?.deadlines || [];
  const overdue = deadlines.filter((d: any) => d.status === 'overdue');
  const upcoming = deadlines.filter((d: any) => d.status === 'upcoming');

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const daysUntil = (dateStr: string) => {
    const diff = Math.ceil((new Date(dateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (diff < 0) return `${Math.abs(diff)} days overdue`;
    if (diff === 0) return 'Due today';
    if (diff === 1) return 'Due tomorrow';
    return `${diff} days`;
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Key Dates & Deadlines</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Stay on top of your ATO obligations. Your accountant manages these for you.
        </p>
      </div>

      {/* Traffic light summary */}
      <div className="grid grid-cols-3 gap-3">
        <Card className={cn(overdue.length > 0 && 'border-red-500/30')}>
          <CardContent className="p-4 text-center">
            <AlertCircle className={cn('h-6 w-6 mx-auto mb-1', overdue.length > 0 ? 'text-red-500' : 'text-muted-foreground/30')} />
            <p className="text-2xl font-bold">{overdue.length}</p>
            <p className="text-xs text-muted-foreground">Overdue</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Clock className="h-6 w-6 mx-auto mb-1 text-amber-500" />
            <p className="text-2xl font-bold">{upcoming.filter((d: any) => {
              const days = Math.ceil((new Date(d.date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
              return days <= 30;
            }).length}</p>
            <p className="text-xs text-muted-foreground">Due in 30 days</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <CheckCircle className="h-6 w-6 mx-auto mb-1 text-green-500" />
            <p className="text-2xl font-bold">{upcoming.length}</p>
            <p className="text-xs text-muted-foreground">Upcoming</p>
          </CardContent>
        </Card>
      </div>

      {/* Deadline list */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <CalendarDays className="h-4 w-4" /> All Deadlines
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {deadlines.map((deadline: any, i: number) => {
              const isOverdue = deadline.status === 'overdue';
              const days = Math.ceil((new Date(deadline.date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
              const isUrgent = days <= 14 && days >= 0;

              return (
                <div key={i} className={cn(
                  'flex items-center justify-between p-3 rounded-lg border',
                  isOverdue ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900/30' :
                  isUrgent ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/30' :
                  'hover:bg-muted/50'
                )}>
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'h-2 w-2 rounded-full',
                      isOverdue ? 'bg-red-500' : isUrgent ? 'bg-amber-500' : 'bg-green-500'
                    )} />
                    <div>
                      <p className="text-sm font-medium">{deadline.name}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(deadline.date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">{deadline.type}</Badge>
                    <span className={cn(
                      'text-xs font-medium',
                      isOverdue ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-muted-foreground'
                    )}>
                      {daysUntil(deadline.date)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
